export interface DnAttribute {
  key: string;
  value: string;
  label: string;
}

const DN_LABELS: Record<string, string> = {
  CN: 'Nombre común',
  O: 'Organización',
  OU: 'Unidad organizativa',
  L: 'Localidad',
  ST: 'Departamento / Estado',
  S: 'Departamento / Estado',
  C: 'País',
  E: 'Correo',
  EMAILADDRESS: 'Correo',
  SERIALNUMBER: 'Número de serie',
  STREET: 'Dirección',
  T: 'Título',
  GIVENNAME: 'Nombre',
  SURNAME: 'Apellido',
  '1.2.840.113549.1.9.1': 'Correo'
};

/**
 * Indica si el DN viene en forma OID/hex (poco legible para UI).
 */
export function isOidStyleDn(dn: string | null | undefined): boolean {
  if (!dn) return false;
  const t = dn.trim();
  return /^\d+(\.\d+)+=/.test(t) || /#\d{2}/.test(t);
}

/**
 * Parsea atributos de un DN (Subject o Issuer).
 * Soporta claves alfabéticas (CN, O…) y OIDs numéricos.
 */
export function parseDnAttributes(dn: string | null | undefined): DnAttribute[] {
  if (!dn || typeof dn !== 'string' || dn === 'N/A') return [];
  const trimmed = dn.trim();
  if (!trimmed) return [];

  const pairs = trimmed.split(/\s*,\s*(?=(?:[A-Za-z][A-Za-z0-9]*|\d+(?:\.\d+)*)=)/);
  const result: DnAttribute[] = [];
  for (const pair of pairs) {
    const eq = pair.indexOf('=');
    if (eq <= 0) continue;
    const key = pair.slice(0, eq).trim();
    let value = pair.slice(eq + 1).trim().replace(/\\,/g, ',');
    if (!key || !value) continue;
    if (value.startsWith('#')) {
      const decoded = decodeAsn1HexString(value);
      if (decoded) value = decoded;
    }
    const keyUp = key.toUpperCase();
    result.push({
      key,
      value,
      label: DN_LABELS[keyUp] || DN_LABELS[key] || key
    });
  }
  return result;
}

/** Decodifica valores ASN.1 hex tipo #161e414e44… (IA5String / UTF8String). */
function decodeAsn1HexString(value: string): string | null {
  if (!value.startsWith('#')) return null;
  const hex = value.slice(1).replace(/\s/g, '');
  if (hex.length < 4 || hex.length % 2 !== 0 || !/^[0-9a-fA-F]+$/.test(hex)) return null;
  try {
    const bytes: number[] = [];
    for (let i = 0; i < hex.length; i += 2) {
      bytes.push(parseInt(hex.slice(i, i + 2), 16));
    }
    // tag (1) + length (1, short form) + content
    if (bytes.length < 3) return null;
    const len = bytes[1];
    if (len <= 0 || 2 + len > bytes.length) return null;
    const content = bytes.slice(2, 2 + len);
    const text = String.fromCharCode(...content);
    if (/^[\x20-\x7E@._+-]+$/.test(text) || /@/.test(text)) return text;
    return new TextDecoder('utf-8', { fatal: false }).decode(new Uint8Array(content));
  } catch {
    return null;
  }
}

export function dnCommonName(dn: string | null | undefined): string | null {
  const cn = parseDnAttributes(dn).find((a) => a.key.toUpperCase() === 'CN');
  return cn?.value?.trim() || null;
}

/**
 * Extrae atributos del subjectDN (ej. E=, SERIALNUMBER=).
 * Formato típico: "CN=Nombre, E=correo@dominio.com, SERIALNUMBER=123456"
 */
export function parseSubjectDN(subjectDN: string | undefined): { email?: string; serialNumber?: string } {
  const result: { email?: string; serialNumber?: string } = {};
  const attrs = parseDnAttributes(subjectDN);
  for (const attr of attrs) {
    const key = attr.key.toUpperCase();
    if (key === 'E' || key === 'EMAILADDRESS' || key === '1.2.840.113549.1.9.1') {
      result.email = attr.value || undefined;
    }
    if (key === 'SERIALNUMBER') {
      result.serialNumber = attr.value || undefined;
    }
  }
  return result;
}

/**
 * Enmascara el correo mostrando solo parte final antes de @ y el dominio.
 * Ejemplo: usuario@gmail.com → XXXXXXXario@gmail.com o XXXXXXXEBA23@GMAIL.COM
 */
export function maskEmail(email: string | undefined, visibleBeforeAt = 6): string {
  if (!email || !email.includes('@')) return email || '***';
  const [local, domain] = email.split('@');
  if (!local || !domain) return '***';
  const keep = Math.min(visibleBeforeAt, local.length);
  const visible = local.slice(-keep);
  const masked = 'X'.repeat(Math.max(1, local.length - keep)) + visible;
  return masked + '@' + domain.toUpperCase();
}

/**
 * Enmascara el número móvil mostrando solo los últimos 4 dígitos.
 * Ejemplo: 3001232224 → XXXXXX2224
 */
export function maskMobile(mobile: string | undefined): string {
  if (!mobile) return '***';
  const digits = mobile.replace(/\D/g, '');
  if (digits.length < 4) return '***';
  return 'X'.repeat(6) + digits.slice(-4);
}
